import { definePolicy, type Policy } from '../kernel';
import { allow, deny } from '../types';

/**
 * THE SALES ORDER DELETE DATA GATES — what the order is attached to, judged BEFORE the Draft-only
 * delete window of `salesOrderPolicy` (the api has always checked them first, in this order).
 *
 *   delete ─▶ hasExternalProducts ─▶ deny "…attached to a purchase order."
 *          ─▶ isDefaultTemplate   ─▶ deny "…default sales order template."
 *          ─▶ allow ─▶ salesOrderPolicy `delete` (Draft only)
 *
 * A separate policy, not two more `SalesOrderFacts`: only the delete door reads these relations,
 * so the header / lifecycle facts read (every edit) does not pay for them.
 */
export type SalesOrderDeletionFacts = {
  /** At least one live, persisted product row is sourced from a purchase order (`external`). */
  hasExternalProducts: boolean;
  /** This order is the source of its organization's DEFAULT sales order template. */
  isDefaultTemplate: boolean;
};

export const salesOrderDeletionPolicy: Policy<SalesOrderDeletionFacts, never, never> = definePolicy<
  SalesOrderDeletionFacts,
  never,
  never
>({
  kind: 'sales order deletion',
  fields: [],
  ops: [],
  requires: {},
  entity: (facts, action) => {
    if (action.type !== 'delete') return allow();

    if (facts.hasExternalProducts) {
      return deny('SALES_ORDER_DELETE_LOCKED', "Can't delete: this order is linked to a purchase order.");
    }

    if (facts.isDefaultTemplate) {
      return deny('SALES_ORDER_DELETE_LOCKED', "The default template can't be deleted.");
    }

    return allow();
  },
});
